import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, TrendingUp, Wallet, Clock, Layers, Download, Plus } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { StatCard, Spinner, PageHeader } from "@/components/common";
import { api, naira, media, fmtDate } from "@/lib/api";

export default function SellerOverview() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/seller/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  const s = d.stats;
  return (
    <div>
      <PageHeader eyebrow="Seller dashboard" title="Overview">
        <Button asChild className="rounded-full" data-testid="dashboard-upload-btn"><Link to="/seller/upload"><Plus size={16} className="mr-1" />Sell a Template</Link></Button>
      </PageHeader>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-3">
        <StatCard testid="stat-total-sales" label="Total sales" value={s.total_sales} icon={ShoppingCart} />
        <StatCard testid="stat-total-earnings" label="Total earnings" value={naira(s.total_earnings)} icon={TrendingUp} accent="text-emerald-500" />
        <StatCard testid="stat-available-balance" label="Available balance" value={naira(s.available_balance)} icon={Wallet} accent="text-primary" />
        <StatCard testid="stat-pending-balance" label="Pending balance" value={naira(s.pending_balance)} icon={Clock} accent="text-amber-500" />
        <StatCard testid="stat-total-templates" label="Total templates" value={s.total_templates} icon={Layers} />
        <StatCard testid="stat-total-downloads" label="Total downloads" value={s.total_downloads} icon={Download} />
      </div>
      <div className="card-surface mt-6 p-5" data-testid="earnings-chart">
        <div className="mb-4 flex items-center justify-between"><p className="font-semibold">Earnings · last 30 days</p><p className="eyebrow">NGN</p></div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.chart}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(262 83% 60%)" stopOpacity={0.4} /><stop offset="100%" stopColor="hsl(262 83% 60%)" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `₦${v >= 1000 ? `${v / 1000}k` : v}`} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} formatter={(v, n) => (n === "earnings" ? naira(v) : v)} />
              <Area type="monotone" dataKey="earnings" stroke="hsl(262 83% 62%)" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="card-surface mt-6">
        <div className="flex items-center justify-between p-5"><p className="font-semibold">Recent sales</p><Link to="/seller/sales" className="text-sm text-primary">View all</Link></div>
        {d.recent_sales.length === 0 ? <p className="px-5 pb-6 text-sm text-muted-foreground">No sales yet — share your templates!</p> : d.recent_sales.map((i) => (
          <div key={i.id} className="flex items-center gap-3 border-t border-border px-5 py-3 text-sm">
            <img src={media(i.cover_image)} alt="" className="h-10 w-9 rounded object-cover" />
            <div className="min-w-0 flex-1"><p className="truncate font-medium">{i.title}</p><p className="text-xs text-muted-foreground">{i.buyer_name} · {fmtDate(i.paid_at)}</p></div>
            <span className="font-semibold text-emerald-500">+{naira(i.seller_earning)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
